import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';

import AIStudioToastCard from './AIStudioToastCard';
import type {
  ShowToastOptions,
  ToastContextValue,
  ToastItem,
} from './ToastContext';
import { ToastContext } from './ToastContext';

const MAX_VISIBLE_TOASTS = 3;
const STACK_OFFSET_PX = 8;
const STACK_SCALE_STEP = 0.04;

const TOAST_CHANNEL_NAME = 'dashboard-ui:global-toast';

type ToastChannelMessage = { id: string; source: string; type: 'dismiss' };

const ToastProvider = ({ children }: { children?: React.ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextIdRef = useRef(0);
  const channelRef = useRef<BroadcastChannel | null>(null);
  // Identifies this provider instance on the channel so it can ignore its
  // own broadcasts.
  const sourceRef = useRef(crypto.randomUUID());
  // Caller-supplied ids — the only ones relayed across tabs.
  const sharedIdsRef = useRef(new Set<string>());

  const dismiss = useCallback((id: string, relay = true) => {
    setToasts(current => current.filter(t => t.id !== id));
    if (relay && sharedIdsRef.current.has(id)) {
      channelRef.current?.postMessage({
        id,
        source: sourceRef.current,
        type: 'dismiss',
      });
    }
  }, []);

  const showToast = useCallback((options: ShowToastOptions) => {
    const id = options.id ?? `toast-${nextIdRef.current++}`;
    if (options.id) {
      sharedIdsRef.current.add(id);
    }
    // Keep all toasts in state so overflowing ones queue up; only the last
    // MAX_VISIBLE_TOASTS render. When a visible one is dismissed, a queued
    // toast takes its place at the back of the stack.
    setToasts(current =>
      current.some(t => t.id === id)
        ? current.map(t => (t.id === id ? { ...options, id } : t))
        : [...current, { ...options, id }],
    );
  }, []);

  useEffect(() => {
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(TOAST_CHANNEL_NAME);
    } catch {
      return;
    }
    channelRef.current = channel;
    channel.onmessage = (event: MessageEvent<ToastChannelMessage>) => {
      const message = event.data;
      if (!message || message.source === sourceRef.current) {
        return;
      }
      // relay=false: the originating tab already broadcast this.
      dismiss(message.id, false);
    };
    return () => {
      channel?.close();
      channelRef.current = null;
    };
  }, [dismiss]);

  const contextValue: ToastContextValue = useMemo(
    () => ({ showToast }),
    [showToast],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <Box
        sx={{
          left: '50%',
          pointerEvents: 'none',
          position: 'fixed',
          top: '16px',
          transform: 'translateX(-50%)',
          width: '470px',
          zIndex: 1600,
        }}
      >
        {toasts.slice(-MAX_VISIBLE_TOASTS).map((toast, index, visible) => {
          // Newest visible toast (last in slice) sits on top at depth 0.
          const depth = visible.length - 1 - index;
          const userOnClick = toast.onClick;
          return (
            <Box
              key={toast.id}
              sx={{
                left: 0,
                position: 'absolute',
                top: 0,
                transform: `translateY(${depth * STACK_OFFSET_PX}px) scale(${
                  1 - depth * STACK_SCALE_STEP
                })`,
                transformOrigin: 'top center',
                transition: 'transform 200ms ease',
                zIndex: visible.length - depth,
              }}
            >
              <AIStudioToastCard
                brand={toast.brand}
                label={toast.label}
                onClick={
                  userOnClick &&
                  (() => {
                    userOnClick();
                    dismiss(toast.id);
                  })
                }
                onClose={() => dismiss(toast.id)}
                preview={toast.preview}
                tone={toast.tone}
              />
            </Box>
          );
        })}
      </Box>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
